import { Component, type ReactNode, type JSX } from "react";
import { useI18n } from "../../i18n";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

function ReloadButton(): JSX.Element {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="mt-6 bg-[var(--color-lime)] px-6 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-void)] hover:brightness-110"
      style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
    >
      {t("pwa.boot.reload")}
    </button>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <FallbackUI />
      );
    }
    return this.props.children;
  }
}

function FallbackUI(): JSX.Element {
  const { t } = useI18n();
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-void)] px-6 text-center"
    >
      <h1 className="font-display text-3xl uppercase tracking-[0.02em] text-[var(--color-lime)]">{t("pwa.boot.failed")}</h1>
      <p className="mt-4 max-w-md font-sans text-[var(--color-ink)]">{t("pwa.boot.body")}</p>
      <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
        <ReloadButton />
        <a
          href="/"
          className="border border-[var(--color-line-strong)] px-6 py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)] transition-colors"
          style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
        >
          {t("common.returnHome")}
        </a>
      </div>
    </div>
  );
}
